import type { ReferencedEntry } from 'contentful';
import { useMemo } from 'react';

import {
  ButtonTheme,
  ExternalLink,
  InternalLink,
  Link,
  LinkVariant,
  TextMediaHeroType,
  TextMediaModuleType,
  Topic,
} from 'api/contentful/contentTypes.cloverdotcom';
import { useBreakpoints } from 'hooks/useBreakpoints';
import { getField } from 'utils/EntryUtil';

/**
 * Currently Fullbleed hero uses different button theme between mobile/tablet and desktop.
 * This overrides topic entry to match up with Figma design.
 */
export const useOverrideTopic = (
  topic?: ReferencedEntry<Topic>,
  moduleType?: TextMediaModuleType,
  heroType?: TextMediaHeroType,
  hasBackground?: boolean,
): ReferencedEntry<Topic> | undefined => {
  const { browser } = useBreakpoints();
  return useMemo(() => {
    const link = getField(topic, 'link') as ReferencedEntry<InternalLink | ExternalLink>;
    const secondaryLinks = getField(topic, 'secondaryLinks') as ReferencedEntry<
      InternalLink | ExternalLink
    >[];
    const isHeadline = moduleType === TextMediaModuleType.HEADLINE;
    const isHero = moduleType === TextMediaModuleType.HERO;

    if (
      !topic ||
      isHeadline ||
      (isHero && (!browser.lessThan.desktop || heroType !== TextMediaHeroType.FULLBLEED))
    ) {
      return undefined;
    }

    return {
      ...topic,
      fields: {
        ...topic.fields,
        link: convertLink(link, isHero, hasBackground),
        secondaryLinks: secondaryLinks?.map(li => convertLink(li, isHero, hasBackground)),
      },
    } as ReferencedEntry<Topic>;
  }, [topic, browser.lessThan.desktop, heroType, moduleType, hasBackground]);
};

function convertLink(
  cta?: ReferencedEntry<Link>,
  isHero?: boolean,
  hasBackground?: boolean,
): ReferencedEntry<Link> | undefined {
  if (!cta?.fields) return undefined;

  const { buttonTheme, buttonOutline, variant } = cta.fields as InternalLink | ExternalLink;
  if (
    (!isHero && buttonTheme) ||
    (!isHero && variant === LinkVariant.TEXT) ||
    (isHero && buttonTheme !== ButtonTheme.IMAGE)
  ) {
    return cta;
  }

  let theme: ButtonTheme | undefined = ButtonTheme.LIGHT;
  let outline = buttonOutline;

  if (!isHero && variant) {
    if (variant === LinkVariant.PRIMARY || variant === LinkVariant.SECONDARY) {
      theme = hasBackground ? ButtonTheme.IMAGE : ButtonTheme.LIGHT;
      if (variant === LinkVariant.SECONDARY) {
        outline = true;
      }
    }
  }

  return {
    ...cta,
    fields: {
      ...cta.fields,
      buttonTheme: theme,
      buttonOutline: outline,
    },
  };
}
